import {Article} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/help_center/v1/article.types';
import {
  SelfServiceFlowSection,
  SelfServiceFlowSection_SectionChild,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/help_center/v1/self_service_flow_section.types';
import {groupBy, keyBy} from '@aboutyou/utils';
import {HttpUrl} from '../../../../shared-deprecated/urlTypes';
import {
  SECTION_ID_TO_ICON_MAPPING,
  SECTION_ID_TO_SECTIONS_DESCRIPTION,
  SSF_SCOPED_PARAM,
} from './constants';

export interface HelpCenterSectionFromIRIS {
  id: number;
  category_id: number;
  name: string;
  parent_section_id?: number;
}

export interface ArticleFromIRIS {
  id: number;
  draft: boolean;
  promoted: boolean;
  section_id: number;
  name: string;
  body: string;
}

export type SectionsFromIRIS = HelpCenterSectionFromIRIS[];
export type ArticlesFromIRIS = ArticleFromIRIS[];

export async function fetchDataFromIRIS<T>(
  entity: string,
  targetUrl: HttpUrl,
  options?: {noScope: boolean},
): Promise<T> {
  const url: HttpUrl = `${targetUrl}${entity}${
    options?.noScope ? '' : SSF_SCOPED_PARAM
  }` as HttpUrl;
  const response = await fetch(url);
  if (response.ok) {
    return await response.json();
  } else {
    throw new Error('Something went wrong fetching from IRIS');
  }
}

export function transformIrisArticleToTdrArticle(
  article: ArticleFromIRIS,
): Article {
  return {
    id: article.id,
    title: article.name,
    description: article.body,
    url: {url: `/h/ssf/${article.section_id}/q-${article.id}`, external: false},
  };
}

function getDescriptionForSection(id: number, sections: SectionsFromIRIS) {
  const linkedSections = SECTION_ID_TO_SECTIONS_DESCRIPTION[id];
  const sectionsById = keyBy(sections, 'id');

  if (linkedSections == null) {
    return;
  }

  return linkedSections
    .map(id => sectionsById[id]?.name)
    .filter(Boolean)
    .join(', ');
}

function buildSectionChildren(
  sections: SectionsFromIRIS,
  articlesBySectionId: {[index: string]: ArticleFromIRIS},
  allSections: SectionsFromIRIS,
): SelfServiceFlowSection_SectionChild[] {
  return sections.map(section => ({
    id: section.id,
    title: section.name,
    description: getDescriptionForSection(section.id, allSections),
    icon: SECTION_ID_TO_ICON_MAPPING[section.id],
    nextLink: {
      url:
        articlesBySectionId[section.id] != null
          ? `/h/ssf/${section.id}/q-${articlesBySectionId[section.id]!.id}`
          : `/h/ssf/${section.id}`,
      external: false,
    },
  }));
}

export function getChildrenForSection(sections: SectionsFromIRIS, id?: number) {
  const sectionsGroupedByParentId = groupBy(sections, 'parent_section_id');
  return sectionsGroupedByParentId[id ?? 'null'] ?? [];
}

export function transformIrisSectionsToFlowSection(
  sections: SectionsFromIRIS,
  articles: ArticlesFromIRIS,
  sectionId?: number,
): {
  section: SelfServiceFlowSection;
  isLastLevel: boolean;
  isFirstLevel: boolean;
} {
  const children = getChildrenForSection(sections, sectionId);

  const articlesBySectionId = keyBy(articles, 'section_id');

  const isFirstLevel = sectionId == null;
  const isLastLevel = children.every(
    child => articlesBySectionId[child.id] != null,
  );

  return {
    isLastLevel,
    isFirstLevel,
    section: {
      children: buildSectionChildren(children, articlesBySectionId, sections),
    },
  };
}
